import React, { useEffect, useMemo, useState } from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import { Tooltip } from 'react-tooltip';
import { format, subDays } from 'date-fns';
import './UserHeatmap.css';

const THEMES = [
  { id: 'blue', label: 'System Blue' },
  { id: 'ocean', label: 'Ocean' },
  { id: 'sunset', label: 'Sunset' },
  { id: 'mono', label: 'Monochrome' }
];

export default function UserHeatmap({ userId, token }) {
  const [data, setData] = useState([]);
  const [streaks, setStreaks] = useState({ currentStreak: 0, longestStreak: 0, lastActive: null });
  const [theme, setTheme] = useState('blue');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default to last 365 days
  const endDate = useMemo(() => new Date(), []);
  const startDate = useMemo(() => subDays(endDate, 364), [endDate]);

  useEffect(() => {
    const fetchHeatmapData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const from = format(startDate, 'yyyy-MM-dd');
        const to = format(endDate, 'yyyy-MM-dd');

        const response = await fetch(`/api/activity/users/${userId}/heatmap?from=${from}&to=${to}`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        const values = result.days.map(d => ({
          date: d.date,
          count: d.totalScore
        }));
        
        setData(values);
        setStreaks(result.streaks || { currentStreak: 0, longestStreak: 0, lastActive: null });
        
      } catch (err) {
        console.error('Error fetching heatmap data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId && token) {
      fetchHeatmapData();
    }
  }, [userId, token, startDate, endDate]);

  function classForValue(v) {
    if (!v || !v.count) return `color-empty`;
    const c = v.count;
    if (c <= 5) return `color-scale-1`;
    if (c <= 15) return `color-scale-2`;
    if (c <= 30) return `color-scale-3`;
    return `color-scale-4`;
  }

  function tooltipDataAttrs(v) {
    if (!v || !v.date) return null;
    return {
      'data-tooltip-id': 'heatmap-tooltip',
      'data-tooltip-content': `${v.date}: ${v.count || 0} pts`
    };
  }

  if (loading) {
    return (
      <div className="nesim-heatmap loading">
        <div className="loading-spinner">Loading heatmap...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nesim-heatmap error">
        <div className="error-message">Error loading heatmap: {error}</div>
      </div>
    );
  }

  return (
    <div className={`nesim-heatmap ${theme}`}>
      <div className="hm-header">
        <h3>Activity Heatmap (last 12 months)</h3>
        <div className="hm-meta">
          <span>Current streak: <strong>{streaks.currentStreak}</strong> days</span>
          <span>Longest streak: <strong>{streaks.longestStreak}</strong> days</span>
          {streaks.lastActive && <span>Last active: <strong>{streaks.lastActive}</strong></span>}
        </div>
        <div className="hm-controls">
          <label htmlFor="theme">Theme</label>
          <select id="theme" value={theme} onChange={e => setTheme(e.target.value)}>
            {THEMES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <CalendarHeatmap
        startDate={startDate}
        endDate={endDate}
        values={data}
        classForValue={classForValue}
        tooltipDataAttrs={tooltipDataAttrs}
        showWeekdayLabels={true}
        gutterSize={3}
      />
      <Tooltip id="heatmap-tooltip" />
      <div className="hm-legend">
        <span>Less</span>
        <span className="legend-box color-scale-1" />
        <span className="legend-box color-scale-2" />
        <span className="legend-box color-scale-3" />
        <span className="legend-box color-scale-4" />
        <span>More</span>
      </div>
    </div>
  );
}
